import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import type { InterfaceSummary } from '@/hooks/useDeviceTrafficSummary';
import { StatTile } from '@/components/ui/stat-tile';
import { formatBits, formatTotal } from './format';
import { faults, portName as name } from './rows';
import { Y_AXIS_WIDTH } from './chart';

const SHOWN = 8;
const ROW_HEIGHT = 26;

/**
 * The "which port" instrument, and the answer to a switch with 125 of them.
 *
 * Horizontal because port names are long — `xe-2/0/0`, `GigabitEthernet1/0/24`
 * — and ranked by **fault**, never by volume: a busy port is normal and a lossy
 * port is the ticket. Every NMS that ranks "top talkers" sends the reader to
 * the uplink that is supposed to be busy.
 *
 * All bars carry one hue. Colour-ramping them by value would spend the identity
 * channel re-encoding what bar length already shows, and would do it on nominal
 * categories, which is the anti-pattern this block exists to avoid.
 *
 * The caption always states the tail, so a reader knows the list is a window
 * onto something longer rather than the whole story.
 */
export function PortRanking({
  rows,
  totalRanked,
  onSelect,
}: {
  rows: InterfaceSummary[];
  totalRanked: number;
  onSelect?: (row: InterfaceSummary) => void;
}) {
  // LAG members are already sorted below everything ranked, and they are a
  // ledger row rather than a candidate: an aggregate and its members would
  // otherwise take four of these eight slots for one flow.
  const ranked = rows.filter((row) => !row.aggregated);
  const faulting = ranked.filter((row) => faults(row) > 0);
  const byFault = faulting.length > 0;
  const shown = (byFault ? faulting : ranked.filter((row) => (row.bitsIn ?? 0) + (row.bitsOut ?? 0) > 0)).slice(0, SHOWN);

  if (shown.length === 0) {
    return (
      <div>
        <h3 className="text-xs font-semibold mb-2">Ports by fault</h3>
        <p className="text-xs text-muted-foreground">
          No port on this device reported errors, discards or traffic in this window.
        </p>
      </div>
    );
  }

  // One bar is not a chart. A single qualifying port is a number, and the form
  // heuristic says a number is a tile.
  if (shown.length === 1) {
    const only = shown[0];
    return (
      <div>
        <h3 className="text-xs font-semibold mb-2">Ports by fault</h3>
        <StatTile
          label={name(only)}
          figures="proportional"
          value={byFault ? formatTotal(faults(only)) : formatBits(Math.max(only.bitsIn ?? 0, only.bitsOut ?? 0))}
          detail={`${only.reason} · the only port of ${String(totalRanked)} with anything to report`}
          tone={byFault ? 'warning' : 'neutral'}
          toneLabel="frames lost"
        />
      </div>
    );
  }

  const data = shown.map((row) => ({
    name: name(row),
    value: byFault ? faults(row) : Math.max(row.bitsIn ?? 0, row.bitsOut ?? 0),
    row,
  }));

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h3 className="text-xs font-semibold">{byFault ? 'Ports by fault' : 'Busiest ports'}</h3>
        <p className="text-[11px] text-muted-foreground">
          {shown.length} of {totalRanked} shown
        </p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {byFault
          ? 'Frames lost to errors and discards, worst first. Errors rank above discards: corruption is a fault, congestion is a capacity question.'
          : 'No errors or discards on any port in this window; showing the busiest instead.'}
      </p>

      <ResponsiveContainer width="100%" height={data.length * ROW_HEIGHT + 28}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 72, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border-subtle)" horizontal={false} />
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            width={Y_AXIS_WIDTH * 2}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            interval={0}
            axisLine={false}
            tickLine={false}
          />
          <Bar
            dataKey="value"
            fill="var(--series-1)"
            isAnimationActive={false}
            radius={[0, 2, 2, 0]}
            cursor={onSelect ? 'pointer' : undefined}
            onClick={onSelect ? (_bar, i) => { onSelect(data[i].row); } : undefined}
          >
            <LabelList
              dataKey="value"
              position="right"
              fontSize={11}
              fill="var(--fg-subtle)"
              formatter={(value) => (typeof value === 'number' ? (byFault ? formatTotal(value) : formatBits(value)) : '')}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <ul className="mt-2 space-y-0.5">
        {shown.map((row) => (
          <li key={row.interfaceId} className="text-[11px] text-muted-foreground">
            <span className="text-foreground">{name(row)}</span> — {row.reason}
            {row.operStatus !== null && row.operStatus !== 'up' ? ` · ${row.operStatus}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
